import V from "voca";

import type { O } from "@scripts/fp-ts";
import { pipe, RA } from "@scripts/fp-ts";
import type { CssType as CssTypeInvestorPortal } from "@scripts/generated/sass-types/investorportal";
import type { CssType as CssTypeReactBase } from "@scripts/generated/sass-types/react-base";
import { fromNullableOrOption } from "@scripts/util/fromNullableOrOption";
import { wrapArr } from "@scripts/util/wrapArr";

import type { KlassOutput } from "./klassOutput";

type _KlassInput<A extends string> = A | O.Option<A> | undefined | null;

export type PartialCssType<A extends CssTypeReactBase> = A;

export type _KlassProp<A extends string = string> =
  KlassOutput | ReadonlyArray<KlassOutput> | _KlassInput<A> | ReadonlyArray<_KlassInput<A>>;

export type KlassProp = _KlassProp<CssTypeReactBase>;

export type ClassName = { className?: string };

const reduceKlassProps = <A extends string>(
  kp: ReadonlyArray<_KlassProp<A>>
): ReadonlyArray<KlassOutput> =>
  pipe(
    kp,
    RA.reduce<_KlassProp<A>, ReadonlyArray<KlassOutput>>([], (acc, _) =>
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      [...acc, ...(pipe(_, wrapArr, RA.filterMap(fromNullableOrOption)) as ReadonlyArray<KlassOutput>)]
    )
  );

export const _klass = <A extends string = CssTypeReactBase>(
  ...kp: ReadonlyArray<_KlassProp<A>>
): ClassName => {
  const className = pipe(
    reduceKlassProps(kp),
    RA.filter(_ => !V.isEmpty(_)),
    _ => _.join(" ")
  );

  return V.isEmpty(className) ? {} : {
    className,
  };
};

export const klass = _klass<CssTypeReactBase>;

export const _klassProp = <A extends string>(
  ...kp: ReadonlyArray<_KlassProp<A>>
): ReadonlyArray<KlassOutput> => reduceKlassProps(kp);

export const klassProp = _klassProp<CssTypeReactBase>;

export const _klassConditional = <A extends string>(
  ...kp: ReadonlyArray<_KlassProp<A>>
) => (condition: boolean): ReadonlyArray<KlassOutput> => condition ? reduceKlassProps(kp) : [];

export const klassConditional = _klassConditional<CssTypeReactBase>;

export type _ApplyKlassNullable<A extends ClassName, KlassPropT> = Omit<A, "className"> & { klass?: KlassPropT };

export type ApplyKlassNullable<A extends ClassName> = _ApplyKlassNullable<A, KlassProp>;

export const invBankKlassProp = _klassProp<CssTypeInvestorPortal>;

export const invBankKlassConditional = _klassConditional<CssTypeInvestorPortal>;
